import { isBrowser } from '../utils';
import type { DeviceContext } from '../types';

function parseUa(ua: string): Pick<DeviceContext, 'os_name' | 'browser_name' | 'browser_version' | 'device_type'> {
  let os_name = 'unknown';
  if (/Windows/i.test(ua)) os_name = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os_name = 'macOS';
  else if (/Android/i.test(ua)) os_name = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os_name = 'iOS';
  else if (/Linux/i.test(ua)) os_name = 'Linux';

  let browser_name = 'unknown';
  let browser_version = '';
  const pairs: Array<[RegExp, string]> = [
    [/Edg\/([\d.]+)/, 'Edge'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/Version\/([\d.]+).*Safari/, 'Safari'],
    [/Safari\/([\d.]+)/, 'Safari'],
  ];
  for (const [re, name] of pairs) {
    const m = ua.match(re);
    if (m) {
      browser_name = name;
      browser_version = m[1] || '';
      break;
    }
  }

  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua);
  const isMobile = /Mobi|iPhone|Android.*Mobile/i.test(ua);
  const device_type: DeviceContext['device_type'] = isTablet
    ? 'tablet'
    : isMobile
      ? 'mobile'
      : 'desktop';

  return { os_name, browser_name, browser_version, device_type };
}

function networkType(): string {
  if (!isBrowser()) return 'unknown';
  const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
  return conn?.effectiveType || 'unknown';
}

let cached: DeviceContext | null = null;

export function getDeviceContext(): DeviceContext {
  if (cached) return cached;
  if (!isBrowser()) {
    cached = {
      device_type: 'unknown',
      os_name: 'unknown',
      browser_name: 'unknown',
      browser_version: '',
      screen_width: 0,
      screen_height: 0,
      language: 'unknown',
      timezone: 'UTC',
      network_type: 'unknown',
    };
    return cached;
  }

  const ua = navigator.userAgent || '';
  const parsed = parseUa(ua);
  cached = {
    ...parsed,
    screen_width: window.screen?.width || 0,
    screen_height: window.screen?.height || 0,
    language: navigator.language || 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    network_type: networkType(),
  };
  return cached;
}
